import { useMemo, useRef, useState } from 'react';
import { Paperclip, X } from 'lucide-react';
import { Button, IconButton, MultiSelect } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import { useEmployeeOptions, useEmployeesByIds } from '@/api/employees';
import { uploadAttachment, NEGOTIATION_ATTACHMENT_PREFIX, type AttachmentPayload } from '@/api/uploads';
import { errorMessage } from './sale-process-error';

/**
 * Assignees + attachments fields for the negotiation dialog — kept out of the
 * dialog file to keep it lean. Assignees search the employee options endpoint
 * and resolve preselected ids to names; attachments upload via the presigned
 * flow and hold the resulting attachment records.
 */

/** Employee multi-select (chips). Searches `/employees/options` and merges the
 * currently-selected ids (resolved to names) so prefilled picks always show. */
export function AssigneesField({ value, onChange }: { value: number[]; onChange: (ids: number[]) => void }) {
  const { data: options = [] } = useEmployeeOptions('');
  const { data: selected = [] } = useEmployeesByIds(value);

  const merged = useMemo(() => {
    const byId = new Map<number, string>();
    for (const e of [...selected, ...options]) byId.set(e.employee_id, e.employee_name);
    return Array.from(byId, ([id, name]) => ({ value: String(id), label: name }));
  }, [options, selected]);

  return (
    <Field label="Assignees" required>
      {() => (
        <MultiSelect
          placeholder="Select assignees"
          options={merged}
          value={value.map(String)}
          onChange={(next) => onChange(next.map(Number))}
        />
      )}
    </Field>
  );
}

/** File attachments — upload each picked file via the presigned flow, list the
 * uploaded records with a remove control. */
export function AttachmentsField({
  value,
  onChange,
}: {
  value: AttachmentPayload[];
  onChange: (next: AttachmentPayload[]) => void;
}) {
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const pick = async (files: FileList | null) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const uploaded: AttachmentPayload[] = [];
      for (const file of Array.from(files)) {
        uploaded.push(await uploadAttachment(file, NEGOTIATION_ATTACHMENT_PREFIX));
      }
      onChange([...value, ...uploaded]);
    } catch (err) {
      toast.error('Upload failed', { description: errorMessage(err) });
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <Field label="Attachments">
      {() => (
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          <input
            ref={inputRef}
            type="file"
            multiple
            hidden
            onChange={(e) => pick(e.target.files)}
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            loading={uploading}
            onClick={() => inputRef.current?.click()}
            style={{ justifySelf: 'start' }}
          >
            <Paperclip size={14} style={{ marginRight: 'var(--vn-space-1)' }} /> Upload files
          </Button>
          {value.length ? (
            <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
              {value.map((a, i) => (
                <div
                  key={`${a.attachment_file_url}-${i}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 'var(--vn-space-2)',
                    padding: 'var(--vn-space-1) var(--vn-space-2)',
                    borderRadius: 'var(--vn-radius-sm)',
                    background: 'var(--vn-bg-subtle)',
                    fontSize: 'var(--vn-font-size-xs)',
                  }}
                >
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {a.attachment_file_name}
                  </span>
                  <IconButton
                    variant="ghost"
                    size="sm"
                    aria-label="Remove"
                    title="Remove"
                    onClick={() => onChange(value.filter((_, j) => j !== i))}
                    style={{ color: 'var(--vn-fg-subtle)' }}
                  >
                    <X size={14} />
                  </IconButton>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      )}
    </Field>
  );
}
